import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  FormEvent,
} from "react";
import { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";
import { ensureAnonymousSession } from "./supabaseClient";
import { LegalModal } from "./LegalModal";
import { renderTextWithLinks } from "./linkify";
import { TermsOfService, PrivacyPolicy } from "./LegalContent";

import type {
  Room,
  Participant,
  Question,
  ChatMessage,
  Poll,
  PollOption,
  PollResponse,
  ModalContent,
  ToolEventPayload,
  ToolControlCommand,
} from "./types";
import {
  OTHER_OPTION_LABEL,
  isOtherOption,
  displayOptionLabel,
  isChoicePoll,
  sortPollsByOrder,
} from "./types";
import {
  UsersIcon,
  HandIcon,
  MessageIcon,
  ChartIcon,
  WrenchIcon,
  ExternalLinkIcon,
  SendIcon,
  TimerIcon,
  TargetIcon,
  DiceIcon,
  PenLineIcon,
  SparklesIcon,
} from "./icons";

type TeacherView = "create" | "room";
type ActiveTab = "questions" | "chat" | "polls" | "participants" | "tools";
type OverlayWindowEntry = { windowId: number; title: string };

const TEACHER_SESSION_KEY = "classlive-teacher-session";

type TeacherSessionData = {
  roomId: string;
  participantId?: string | null;
  displayName: string;
  role: "host";
  activeFeatures?: ActiveTab[];
  lastTab?: ActiveTab;
};

type ToolControlRequest = {
  id: number;
  command: ToolControlCommand;
};

const isActiveTabValue = (value: unknown): value is ActiveTab =>
  value === "participants" ||
  value === "questions" ||
  value === "chat" ||
  value === "polls" ||
  value === "tools";

const parsePollAnswer = (answer: unknown) => {
  if (Array.isArray(answer)) {
    return answer;
  }
  if (typeof answer === "string") {
    const trimmed = answer.trim();
    if (!trimmed) {
      return "";
    }
    try {
      return JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  }
  return answer;
};

const answerMatchesOption = (answer: unknown, optionId: string) => {
  const value = parsePollAnswer(answer);
  if (Array.isArray(value)) {
    return value.includes(optionId);
  }
  return value === optionId;
};

const extractOtherText = (answer: unknown): string | null => {
  const value = parsePollAnswer(answer);
  if (!Array.isArray(value)) {
    return null;
  }
  for (const item of value) {
    if (
      item &&
      typeof item === "object" &&
      typeof (item as { otherText?: unknown }).otherText === "string"
    ) {
      return (item as { otherText: string }).otherText;
    }
  }
  return null;
};

const stringifyPollAnswer = (answer: unknown) => {
  const value = parsePollAnswer(answer);
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export function TeacherApp() {
  const [view, setView] = useState<TeacherView>("create");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [pollResponses, setPollResponses] = useState<
    Record<string, PollResponse[]>
  >({});
  const [teacherName, setTeacherName] = useState("");
  const [tab, setTab] = useState<ActiveTab>("participants");
  const [activeFeatures, setActiveFeatures] = useState<Set<ActiveTab>>(
    new Set()
  );
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [authReady, setAuthReady] = useState(false);
  const [modal, setModal] = useState<ModalContent | null>(null);
  const [rejoinCode, setRejoinCode] = useState(""); // 재입장 코드 상태 추가
  const [showExitModal, setShowExitModal] = useState(false);
  const [exportSelections, setExportSelections] = useState({
    questions: true,
    chat: true,
    polls: true,
  });
  const [legalModal, setLegalModal] = useState<"tos" | "privacy" | null>(null);

  const downloadCSV = (content: string, filename: string) => {
    const bom = "\uFEFF";
    const blob = new Blob([bom + content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadRecords = () => {
    const timestamp = new Date().toISOString().slice(0, 10);
    let combinedCSV = "";

    if (exportSelections.questions && questions.length > 0) {
      combinedCSV += "### 질문 기록 ###\n";
      combinedCSV += "작성자,내용,추천수,상태,작성시간\n";
      questions.forEach((q) => {
        const author = q.author_name || participantNameMap.get(q.author_id) || "알 수 없음";
        const content = q.body.replace(/"/g, '""');
        const time = new Date(q.created_at).toLocaleString();
        const statusStr = q.status === "answered" ? "답변완료" : (q.status === "open" ? "미답변" : q.status);
        combinedCSV += `"${author}","${content}",${q.upvotes || 0},"${statusStr}","${time}"\n`;
      });
      combinedCSV += "\n\n";
    }

    if (exportSelections.chat && messages.length > 0) {
      combinedCSV += "### 채팅 기록 ###\n";
      combinedCSV += "작성자,내용,작성시간\n";
      messages.forEach((m) => {
        const author = m.sender_name || participantNameMap.get(m.sender_id) || "알 수 없음";
        const content = m.body.replace(/"/g, '""');
        const time = new Date(m.created_at).toLocaleString();
        combinedCSV += `"${author}","${content}","${time}"\n`;
      });
      combinedCSV += "\n\n";
    }

    if (exportSelections.polls && polls.length > 0) {
      combinedCSV += "### 설문 조사 결과 기록 ###\n";
      combinedCSV += "설문 분류,설문 내용,작성자,응답 내용/선택지\n";
      polls.forEach((p) => {
        const typeLabel =
          p.type === "multiple_choice"
            ? "객관식"
            : p.type === "checkbox"
              ? "객관식(복수)"
              : p.type === "word_cloud"
                ? "단어장"
                : "주관식";
        const prompt = p.prompt.replace(/"/g, '""');
        const responses = pollResponses[p.id] || [];

        responses.forEach(r => {
          const author = participantNameMap.get(r.participant_id) || "알 수 없음";
          let answerText = "";

          if (isChoicePoll(p.type)) {
            const answerVal = parsePollAnswer(r.answer);
            const selectedIds = Array.isArray(answerVal) ? answerVal : [answerVal];
            const otherText = extractOtherText(r.answer);
            const selectedLabels = (p.options || [])
              .filter(opt => selectedIds.includes(opt.id))
              .map(opt =>
                isOtherOption(opt.label) && otherText
                  ? `기타(${otherText})`
                  : displayOptionLabel(opt.label)
              )
              .join(", ");
            answerText = selectedLabels || "알 수 없음";
          } else {
            answerText = stringifyPollAnswer(r.answer);
          }
          answerText = answerText.replace(/"/g, '""');
          combinedCSV += `"[개별응답] ${typeLabel}","${prompt}","${author}","${answerText}"\n`;
        });

        combinedCSV += `"[통계 요약] ${typeLabel}","${prompt}","",""\n`;

        if (isChoicePoll(p.type)) {
          const counts: Record<string, number> = {};
          (p.options || []).forEach(o => counts[o.id] = 0);
          responses.forEach(r => {
            const answerVal = parsePollAnswer(r.answer);
            const selectedIds = Array.isArray(answerVal) ? answerVal : [answerVal];
            selectedIds.forEach(id => {
              if (counts[id] !== undefined) counts[id]++;
            });
          });
          (p.options || []).forEach(o => {
            combinedCSV += `"[통계 결과]","","","선택지 [${displayOptionLabel(o.label).replace(/"/g, '""')}] : ${counts[o.id]} 명"\n`;
          });
        } else if (p.type === "word_cloud") {
          const wordCounts: Record<string, number> = {};
          responses.forEach(r => {
            const w = stringifyPollAnswer(r.answer).trim();
            if (w) wordCounts[w] = (wordCounts[w] || 0) + 1;
          });
          Object.entries(wordCounts).sort((a, b) => b[1] - a[1]).forEach(([word, count]) => {
            combinedCSV += `"[통계 결과]","","","단어 [${word.replace(/"/g, '""')}] : ${count} 번"\n`;
          });
        }
        combinedCSV += `\n`;
      });
    }

    if (combinedCSV) {
      downloadCSV(combinedCSV, `교실기록_통합_${timestamp}.csv`);
    }
  };

  const executeExitRoom = async () => {
    try {
      if (room) {
        await Promise.all([
          supabase.from("room_messages").delete().eq("room_id", room.id),
          supabase.from("room_questions").delete().eq("room_id", room.id),
          supabase.from("room_poll_responses").delete().in("poll_id", polls.map((p) => p.id)),
          supabase.from("room_poll_options").delete().in("poll_id", polls.map((p) => p.id)),
          supabase.from("room_polls").delete().eq("room_id", room.id),
          supabase.from("room_participants").delete().eq("room_id", room.id),
          supabase.from("rooms").delete().eq("id", room.id),
        ]);
      }
      localStorage.removeItem(TEACHER_SESSION_KEY);
      setRoom(null);
      setParticipants([]);
      setQuestions([]);
      setMessages([]);
      setPolls([]);
      setActiveFeatures(new Set());
      setView("create");
      setShowExitModal(false);
    } catch (error) {
      console.error("방 삭제 중 오류:", error);
      alert("방을 삭제하지 못했어요. 다시 시도해주세요.");
    }
  };

  // 익명 참여 설정 상태
  const [isAnonJoin, setIsAnonJoin] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("classlive-anon-join") === "true";
    }
    return false;
  });
  const [customRoles, setCustomRoles] = useState<string[]>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("classlive-custom-roles");
      if (stored) return JSON.parse(stored);
    }
    return ["학생", "학부모", "수강생"];
  });

  // 익명 설정 로컬스토리지 저장
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("classlive-anon-join", isAnonJoin.toString());
    }
  }, [isAnonJoin]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("classlive-custom-roles", JSON.stringify(customRoles));
    }
  }, [customRoles]);

  const realtimeChannelRef = useRef<RealtimeChannel | null>(null);

  const participantNameMap = useMemo(() => {
    const map = new Map<string, string>();
    participants.forEach((participant) => {
      map.set(participant.id, participant.display_name);
    });
    return map;
  }, [participants]);

  // 교사 페이지 접속 시 익명 인증 처리
  useEffect(() => {
    let cancelled = false;

    const ensureAuth = async () => {
      try {
        const { user } = await ensureAnonymousSession();
        if (!cancelled) {
          setCurrentUser(user);
        }
      } catch (error) {
        console.error("교사 익명 인증 실패:", error);
        if (!cancelled) {
          setError("인증하지 못했어요. 잠시 후 다시 시도해주세요.");
        }
      } finally {
        if (!cancelled) {
          setAuthReady(true);
        }
      }
    };
    ensureAuth();

    return () => {
      cancelled = true;
    };
  }, []);
  const realtimeJoinPromiseRef = useRef<Promise<void> | null>(null);
  const modalNavigationRef = useRef<{
    questionIds: string[];
    currentIndex: number;
  } | null>(null);
  const modalQuestionRef = useRef<Question | null>(null);
  const sessionRestoreAttemptedRef = useRef(false);

  const readStoredSession = useCallback((): TeacherSessionData | null => {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      const raw = localStorage.getItem(TEACHER_SESSION_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as TeacherSessionData;
    } catch (storageError) {
      console.error("세션 데이터 읽기 실패:", storageError);
      return null;
    }
  }, []);

  const writeStoredSession = useCallback((data: TeacherSessionData) => {
    if (typeof window === "undefined") {
      return;
    }
    localStorage.setItem(TEACHER_SESSION_KEY, JSON.stringify(data));
  }, []);

  const patchStoredSession = useCallback(
    (patch: Partial<TeacherSessionData>) => {
      if (typeof window === "undefined") {
        return;
      }
      const existing = readStoredSession();
      if (!existing) {
        return;
      }
      const next = { ...existing, ...patch } as TeacherSessionData;
      writeStoredSession(next);
    },
    [readStoredSession, writeStoredSession]
  );

  // QR 코드 관련 상태
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">(
    "idle"
  );
  const [toolControlRequest, setToolControlRequest] =
    useState<ToolControlRequest | null>(null);

  // Overlay target window (extension only — null until window list received)
  const [overlayWindows, setOverlayWindows] = useState<OverlayWindowEntry[] | null>(null);
  const [overlayTargetId, setOverlayTargetId] = useState<number | null>(null);

  const postToExtension = useCallback((message: unknown) => {
    ////console.log("postToExtension 호출됨:", message);

    // iframe 환경 확인
    const isInIframe = window.parent !== window || window.top !== window;
    ////console.log("iframe 환경:", isInIframe);

    // iframe 환경에서 부모 창으로 메시지 전송
    const targets = new Set<Window>();
    if (window.parent && window.parent !== window) {
      targets.add(window.parent);
      //console.log("window.parent 추가됨");
    }
    if (window.top && window.top !== window && window.top !== window.parent) {
      targets.add(window.top);
      //console.log("window.top 추가됨");
    }

    //console.log("전송 대상 수:", targets.size);

    if (targets.size === 0) {
      //console.log("전송 대상이 없음 - iframe 환경이 아님");
      return false;
    }

    let delivered = false;
    targets.forEach((target) => {
      try {
        const messageToSend = {
          ...(typeof message === "object" && message !== null ? message : {}),
          source: "classlive-teacher-app",
          timestamp: Date.now(),
        };
        //console.log("메시지 전송 시도:", messageToSend);
        target.postMessage(messageToSend, "*");
        delivered = true;
        //console.log("메시지 전송 성공");
      } catch (targetError) {
        console.error("확장 프로그램으로 메시지 전달 실패:", targetError);
      }
    });

    return delivered;
  }, []);

  // QR 모달 표시 함수
  const handleShowQrModal = useCallback(
    (qrDataUrl: string, code: string) => {
      //console.log("QR 모달 표시 시도:", { qrDataUrl, code });

      const delivered = postToExtension({
        type: "SHOW_QR_MODAL",
        payload: { qrDataUrl, code },
      });

      //console.log("QR 모달 메시지 전달 결과:", delivered);

      if (!delivered) {
        //console.log("확장으로 전달 실패, 폴백 사용");
        // 폴백: 새 창에서 QR 코드 표시
        const qrWindow = window.open("", "_blank", "width=400,height=500");
        if (qrWindow) {
          //console.log("새 창에서 QR 코드 표시");
          qrWindow.document.write(`
            <html>
              <head><title>참여 QR 코드</title></head>
              <body style="text-align: center; padding: 20px; font-family: Arial, sans-serif;">
                <h2>참여 코드: ${code}</h2>
                <img src="${qrDataUrl}" alt="QR 코드" style="max-width: 300px; height: auto;" />
                <p>QR 코드를 스캔하거나 코드를 직접 입력하세요.</p>
              </body>
            </html>
          `);
        } else {
          //console.log("새 창 열기 실패, alert 사용");
          alert(`참여 코드: ${code}\nQR 코드 URL: ${qrDataUrl}`);
        }
      } else {
        //console.log("확장으로 QR 모달 메시지 전달 성공");
      }
    },
    [postToExtension]
  );

  const sendModalUpdate = useCallback(
    (nextModal: ModalContent | null) => {
      postToExtension({
        type: "classlive:modal-update",
        payload: {
          modal: nextModal,
          canControl: true,
        },
      });
    },
    [postToExtension]
  );

  const sendToolEvent = useCallback(
    (event: ToolEventPayload) => {
      postToExtension({
        type: "classlive:tool-event",
        payload: event,
      });
    },
    [postToExtension]
  );

  const handleConsumeToolControl = useCallback(() => {
    setToolControlRequest(null);
  }, []);

  const closeModal = useCallback(() => {
    setModal(null);
    modalNavigationRef.current = null;
    modalQuestionRef.current = null;
    sendModalUpdate(null);
  }, [sendModalUpdate]);

  const openQuestionModal = useCallback(
    (question: Question, ordered: Question[]) => {
      const questionIds = ordered.map((item) => item.id);
      const currentIndex = questionIds.indexOf(question.id);
      const modalPayload: ModalContent = {
        kind: "question",
        question,
        navigation: {
          questionIds,
          currentIndex,
        },
      };

      modalNavigationRef.current = {
        questionIds,
        currentIndex,
      };
      modalQuestionRef.current = question;
      setModal(modalPayload);
      sendModalUpdate(modalPayload);
    },
    [sendModalUpdate]
  );

  const handleQuestionModalAction = useCallback(
    async (action: "next" | "prev" | "complete" | "close") => {
      const currentQuestion = modalQuestionRef.current;
      if (!currentQuestion) {
        closeModal();
        return;
      }

      if (action === "close") {
        closeModal();
        return;
      }

      const ordered = questions
        .filter((item) => item.status !== "dismissed")
        .sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

      if (ordered.length === 0) {
        closeModal();
        return;
      }

      const ids = ordered.map((item) => item.id);
      const currentIndex = ids.indexOf(currentQuestion.id);

      if (action === "next" || action === "prev") {
        if (currentIndex === -1) {
          closeModal();
          return;
        }
        const delta = action === "next" ? 1 : -1;
        const target = ordered[currentIndex + delta];
        if (!target) {
          return;
        }
        modalNavigationRef.current = {
          questionIds: ids,
          currentIndex: currentIndex + delta,
        };
        modalQuestionRef.current = target;
        openQuestionModal(target, ordered);
        return;
      }

      if (action === "complete") {
        try {
          await supabase
            .from("room_questions")
            .update({ status: "answered" })
            .eq("id", currentQuestion.id);
        } catch (updateError) {
          console.error("질문 상태 업데이트 실패:", updateError);
        }
        closeModal();
      }
    },
    [closeModal, openQuestionModal, questions]
  );

  useEffect(() => {
    if (!modal || modal.kind !== "question") {
      return;
    }

    const current = questions.find((item) => item.id === modal.question?.id);
    if (!current || !modal.question) {
      closeModal();
      return;
    }

    if (current === modal.question) {
      return;
    }

    const ordered = questions
      .filter((item) => item.status !== "dismissed")
      .sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

    openQuestionModal(current, ordered);
  }, [closeModal, modal, openQuestionModal, questions]);

  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== "object") {
        return;
      }

      // 확장에서 오는 메시지만 처리
      if ((data as { source?: string }).source !== "classlive-extension") {
        return;
      }

      const messageType = (data as { type?: string }).type;

      if (messageType === "classlive:question-modal-action") {
        const action = (data as { action?: string }).action;
        if (
          action === "next" ||
          action === "prev" ||
          action === "complete" ||
          action === "close"
        ) {
          void handleQuestionModalAction(action);
        }
      } else if (messageType === "classlive:sidebar-state-changed") {
        // 사이드바 상태 변경 알림 처리
        const isOpen = (data as { open?: boolean }).open;
      } else if (messageType === "MODAL_ACTION") {
        const action = (data as { action?: string }).action;
        //console.log("MODAL_ACTION 받음:", action);
        if (action === "close") {
          //console.log("모달 닫기 실행");
          closeModal();
        }
      } else if (messageType === "classlive:tool-control") {
        const command = (data as { command?: ToolControlCommand }).command;
        if (command) {
          setToolControlRequest({
            id: Date.now(),
            command,
          });
        }
      } else if (messageType === "classlive:window-list") {
        const windows = (data as { windows?: OverlayWindowEntry[] }).windows ?? [];
        setOverlayWindows(windows);
        // If selected window is no longer available, reset to current
        setOverlayTargetId((prev) => {
          if (prev !== null && !windows.find((w) => w.windowId === prev)) {
            return null;
          }
          return prev;
        });
      }
    };

    window.addEventListener("message", messageHandler);
    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, [handleQuestionModalAction, closeModal]);

  // QR 코드 생성
  const generateQrCode = useCallback(async (url: string) => {
    try {
      // 간단한 QR 코드 생성 (실제로는 QR 라이브러리 사용)
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
        url
      )}`;
      setQrDataUrl(qrUrl);
    } catch (error) {
      console.error("QR 코드 생성 실패:", error);
    }
  }, []);

  useEffect(() => {
    if (!room) {
      return;
    }
    patchStoredSession({
      activeFeatures: Array.from(activeFeatures),
    });
  }, [activeFeatures, patchStoredSession, room?.id]);

  useEffect(() => {
    if (!room) {
      return;
    }
    patchStoredSession({ lastTab: tab });
  }, [patchStoredSession, room?.id, tab]);

  useEffect(() => {
    if (!room) {
      return;
    }
    patchStoredSession({ displayName: teacherName });
  }, [patchStoredSession, room?.id, teacherName]);

  // 방 생성
  const handleCreateRoom = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!teacherName.trim()) {
        setError("교사 이름을 입력해주세요.");
        return;
      }
      if (!currentUser) {
        setError("인증이 진행 중입니다. 잠시 후 다시 시도해주세요.");
        return;
      }
      // 통합 로그인: 방을 만들 때만 SsamFlow 로그인을 확인해요. 닫으면 방을 만들지 않아요.
      if (!(await (window.SFLogin?.require({ reason: "방을 만들려면 로그인해 주세요" }) ?? true))) return;

      setLoading(true);
      setError(null);

      try {
        const { user: sessionUser } = await ensureAnonymousSession();
        setCurrentUser(sessionUser);
        const code = Math.floor(100000 + Math.random() * 900000).toString();

        const { data: roomData, error: roomError } = await supabase
          .from("rooms")
          .insert({
            code,
            title: `${teacherName}의 교실`,
            host_user_id: sessionUser.id,
            status: "active",
            expires_at: new Date(
              Date.now() + 24 * 60 * 60 * 1000
            ).toISOString(),
          })
          .select("*")
          .single();

        if (roomError) throw roomError;

        const { data: participantData, error: participantError } = await supabase
          .from("room_participants")
          .insert({
            room_id: roomData.id,
            profile_id: sessionUser.id,
            display_name: teacherName,
            role: "host",
          })
          .select("*")
          .single();

        if (participantError) throw participantError;

        const initialTab: ActiveTab = "participants";

        setRoom(roomData);
        setView("room");
        setTab(initialTab);
        setActiveFeatures(new Set<ActiveTab>());

        writeStoredSession({
          roomId: roomData.id,
          participantId: participantData.id ?? null,
          displayName: teacherName,
          role: "host",
          activeFeatures: [],
          lastTab: initialTab,
        });

        const joinUrl = `${window.location.origin}?code=${roomData.code}`;
        generateQrCode(joinUrl);
      } catch (error) {
        console.error("방 생성 오류:", error);
        setError("방을 만들지 못했어요. 다시 시도해주세요.");
      } finally {
        setLoading(false);
      }
    },
    [teacherName, currentUser, generateQrCode, writeStoredSession]
  );

  const handleRejoinRoom = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rejoinCode.trim() || !currentUser) {
      setError("코드를 입력하고 인증을 기다려주세요.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const code = rejoinCode.trim();
      const desiredName = teacherName.trim();

      const { data: rejoinedRoom, error: rejoinError } = await supabase.rpc(
        "teacher_rejoin_room",
        {
          room_code: code,
          display_name: desiredName || null,
        }
      );

      if (rejoinError || !rejoinedRoom) {
        if (rejoinError) {
          if (rejoinError.message === "room_not_found") {
            throw new Error("이 코드로 열린 방을 찾지 못했어요.");
          }
          if (rejoinError.message === "room_not_active") {
            throw new Error("이 방은 종료됐거나 비활성화됐어요.");
          }
          if (rejoinError.message === "not_authenticated") {
            throw new Error("인증 정보를 확인한 후 다시 시도해주세요.");
          }
          throw new Error(rejoinError.message);
        }
        throw new Error("다시 들어가지 못했어요. 다시 시도해주세요.");
      }

      const { data: hostParticipant, error: hostError } = await supabase
        .from("room_participants")
        .select("*")
        .eq("room_id", (rejoinedRoom as Room).id)
        .eq("role", "host")
        .maybeSingle();

      if (hostError || !hostParticipant) {
        throw hostError ?? new Error("교사 정보를 불러오지 못했어요.");
      }

      const name =
        hostParticipant.display_name || desiredName || teacherName || "교사";

      const roomInfo = rejoinedRoom as Room;
      const restoredFeatures = new Set<ActiveTab>();
      if (isActiveTabValue(roomInfo.active_feature)) {
        restoredFeatures.add(roomInfo.active_feature);
      }

      const nextActiveFeature =
        restoredFeatures.size > 0
          ? (restoredFeatures.values().next().value as ActiveTab | undefined)
          : undefined;
      const nextTab = nextActiveFeature ?? tab;

      setRoom(roomInfo);
      setView("room");
      setTeacherName(name);
      setActiveFeatures(restoredFeatures);
      if (nextTab !== tab) {
        setTab(nextTab);
      }

      writeStoredSession({
        roomId: roomInfo.id,
        participantId: hostParticipant.id,
        displayName: name,
        role: "host",
        activeFeatures: Array.from(restoredFeatures),
        lastTab: nextTab,
      });

      const joinUrl = `${window.location.origin}?code=${roomInfo.code}`;
      generateQrCode(joinUrl);

    } catch (error) {
      console.error("재입장 오류:", error);
      setError(error instanceof Error ? error.message : "다시 들어가지 못했어요.");
    } finally {
      setLoading(false);
    }
  }, [rejoinCode, currentUser, generateQrCode, teacherName, writeStoredSession, tab]);

  // 링크 복사 (iframe 환경에서 안전한 방식)
  const handleCopyLink = useCallback(async (textToCopy: string) => {
    if (!room) {
      console.error("방 정보가 없음");
      return;
    }

    try {
      // iframe 환경에서는 확장을 통해 복사
      const delivered = postToExtension({
        type: "COPY_TO_CLIPBOARD",
        text: textToCopy,
      });

      //console.log("확장 전달 결과:", delivered);

      if (delivered) {
        //console.log("확장을 통한 복사 성공");
        setCopyState("copied");
        setTimeout(() => setCopyState("idle"), 2000);
      } else {
        // 일반 웹페이지에서 실행 중인 경우
        await navigator.clipboard.writeText(textToCopy);
        setCopyState("copied");
        setTimeout(() => setCopyState("idle"), 2000);
      }
    } catch (error) {
      console.error("링크 복사 실패:", error);
      // 폴백: 텍스트 선택
      const textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        const success = document.execCommand("copy");
        //console.log("execCommand 복사 결과:", success);
        if (success) {
          setCopyState("copied");
          setTimeout(() => setCopyState("idle"), 2000);
        } else {
          setCopyState("error");
          setTimeout(() => setCopyState("idle"), 3000);
        }
      } catch (fallbackError) {
        console.error("폴백 복사도 실패:", fallbackError);
        setCopyState("error");
        setTimeout(() => setCopyState("idle"), 3000);
      }
      document.body.removeChild(textArea);
    }
  }, [room, postToExtension]);

  // 기능 시작/중지
  const broadcastFeatureChange = useCallback(
    async (
      event: "feature_started" | "feature_stopped",
      feature: ActiveTab
    ) => {
      if (!room) return;

      const channel = realtimeChannelRef.current;
      if (!channel) {
        console.warn("실시간 채널이 준비되지 않았습니다.");
        return;
      }

      try {
        await (realtimeJoinPromiseRef.current ?? Promise.resolve());
      } catch (error) {
        console.warn(
          "실시간 채널 구독이 활성화되지 않아 브로드캐스트를 건너뜁니다.",
          error
        );
        return;
      }

      try {
        const sendResult = await channel.send({
          type: "broadcast",
          event,
          payload: { feature },
        });

        if (
          typeof sendResult === "object" &&
          sendResult !== null &&
          "status" in sendResult &&
          (sendResult as { status?: string }).status &&
          (sendResult as { status?: string }).status !== "ok"
        ) {
          console.warn("브로드캐스트 결과 상태:", sendResult);
        }
      } catch (error) {
        console.error("브로드캐스트 실패:", error);
      }
    },
    [room]
  );

  const handleStartFeature = useCallback(
    async (feature: ActiveTab) => {
      setActiveFeatures((prev) => new Set([...prev, feature]));
      await broadcastFeatureChange("feature_started", feature);
      if (room) {
        await supabase.from("rooms").update({ active_feature: feature }).eq("id", room.id);
      }
    },
    [broadcastFeatureChange, room]
  );

  const handleStopFeature = useCallback(
    async (feature: ActiveTab) => {
      setActiveFeatures((prev) => {
        const next = new Set(prev);
        next.delete(feature);
        return next;
      });

      await broadcastFeatureChange("feature_stopped", feature);
      if (room) {
        await supabase.from("rooms").update({ active_feature: null }).eq("id", room.id);
      }
    },
    [broadcastFeatureChange]
  );

  const buildPollResultsModal = useCallback(
    (pollId: string): ModalContent | null => {
      const poll = polls.find((p) => p.id === pollId);
      if (!poll) {
        return null;
      }

      const responses = pollResponses[pollId] || [];

      const enrichedResponses = responses.map((response) => ({
        ...response,
        participant_name: poll.is_anonymous
          ? null
          : participantNameMap.get(response.participant_id) ?? "알 수 없음",
      }));

      const enrichedOptions = poll.options
        ? poll.options.map((option) => {
          if (poll.is_anonymous) {
            return { ...option, responders: undefined };
          }
          const responders = responses
            .filter((response) => {
              return answerMatchesOption(response.answer, option.id);
            })
            .map((response) => ({
              id: response.participant_id,
              display_name:
                participantNameMap.get(response.participant_id) ?? "알 수 없음",
            }));
          return { ...option, responders };
        })
        : undefined;

      return {
        kind: "poll_results",
        poll: enrichedOptions ? { ...poll, options: enrichedOptions } : poll,
        pollResponses: enrichedResponses,
        participantCount: participants.filter((p) => p.role !== "host").length,
      };
    },
    [participantNameMap, participants, polls, pollResponses]
  );

  const presentedPollId =
    modal?.kind === "poll_results" ? modal.poll?.id ?? null : null;
  const lastPollPayloadRef = useRef<string | null>(null);

  const handlePresentPollResults = useCallback(
    (pollId: string) => {
      const modalPayload = buildPollResultsModal(pollId);
      if (!modalPayload) {
        console.error("설문을 찾을 수 없습니다:", pollId);
        return;
      }

      lastPollPayloadRef.current = JSON.stringify(modalPayload);
      setModal(modalPayload);
      sendModalUpdate(modalPayload);
    },
    [buildPollResultsModal, sendModalUpdate]
  );

  // 결과 화면이 떠 있는 동안 새 응답이 들어오면 메인 화면 오버레이도 실시간 갱신
  useEffect(() => {
    if (!presentedPollId) {
      lastPollPayloadRef.current = null;
      return;
    }
    const timer = window.setTimeout(() => {
      const modalPayload = buildPollResultsModal(presentedPollId);
      if (!modalPayload) {
        closeModal();
        return;
      }
      const signature = JSON.stringify(modalPayload);
      if (signature === lastPollPayloadRef.current) {
        return;
      }
      lastPollPayloadRef.current = signature;
      setModal(modalPayload);
      sendModalUpdate(modalPayload);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [buildPollResultsModal, closeModal, presentedPollId, sendModalUpdate]);

  // 실시간 데이터 구독
  useEffect(() => {
    if (!room) return;

    const channelName = `room-${room.id}`;
    const channel = supabase.channel(channelName, {
      config: {
        broadcast: { ack: true },
        presence: { key: currentUser?.id ?? "teacher" },
      },
    });

    realtimeChannelRef.current = channel;

    let resolveJoin: (() => void) | null = null;
    let rejectJoin: ((reason?: unknown) => void) | null = null;
    const joinPromise = new Promise<void>((resolve, reject) => {
      resolveJoin = resolve;
      rejectJoin = reject;
    });
    realtimeJoinPromiseRef.current = joinPromise;

    const refreshParticipants = async () => {
      const { data, error } = await supabase
        .from("room_participants")
        .select("*")
        .eq("room_id", room.id)
        .order("joined_at", { ascending: true });
      if (error) {
        console.error("참여자 목록 조회 실패:", error);
        return;
      }
      if (data) {
        const activeParticipants = data.filter((participant) => !participant.left_at);
        setParticipants(activeParticipants);
      }
    };

    const refreshQuestions = async () => {
      const { data } = await supabase
        .from("room_questions")
        .select("*")
        .eq("room_id", room.id)
        .order("created_at", { ascending: false });
      if (data) {
        setQuestions(data);
      }
    };

    const refreshMessages = async () => {
      const { data } = await supabase
        .from("room_messages")
        .select("*")
        .eq("room_id", room.id)
        .order("created_at", { ascending: true });
      if (data) {
        setMessages(data);
      }
    };

    const refreshPolls = async () => {
      const { data } = await supabase
        .from("room_polls")
        .select(`*, room_poll_options (*)`)
        .eq("room_id", room.id)
        .order("created_at", { ascending: true });
      if (data) {
        const pollsWithOptions = data.map((poll) => ({
          ...poll,
          options: (poll.room_poll_options || []).slice().sort(
            (a: PollOption, b: PollOption) => a.order_index - b.order_index
          ),
        }));
        setPolls(sortPollsByOrder(pollsWithOptions as Poll[]));
        // 설문 목록이 업데이트되면 응답 데이터도 다시 로드
        await refreshPollResponses();
      }
    };

    const refreshPollResponses = async () => {
      // 먼저 현재 방의 모든 설문을 가져옴
      const { data: pollsData } = await supabase
        .from("room_polls")
        .select("id")
        .eq("room_id", room.id);

      if (!pollsData || pollsData.length === 0) {
        setPollResponses({});
        return;
      }

      const pollIds = pollsData.map((p) => p.id);

      const { data } = await supabase
        .from("room_poll_responses")
        .select("*")
        .in("poll_id", pollIds);

      if (data) {
        const responsesByPoll = data.reduce((acc, response) => {
          if (!acc[response.poll_id]) {
            acc[response.poll_id] = [];
          }
          acc[response.poll_id].push(response);
          return acc;
        }, {} as Record<string, PollResponse[]>);

        setPollResponses(responsesByPoll);
      }
    };

    channel
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "room_participants",
          filter: `room_id=eq.${room.id}`,
        },
        refreshParticipants
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "room_questions",
          filter: `room_id=eq.${room.id}`,
        },
        refreshQuestions
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "room_messages",
          filter: `room_id=eq.${room.id}`,
        },
        refreshMessages
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "room_polls",
          filter: `room_id=eq.${room.id}`,
        },
        refreshPolls
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "room_poll_responses",
        },
        refreshPollResponses
      )
      .on("broadcast", { event: "feature_started" }, (payload) => {
        const feature = (payload as { feature?: string })?.feature;
        if (isActiveTabValue(feature)) {
          setActiveFeatures((prev) => new Set([...prev, feature]));
        }
      })
      .on("broadcast", { event: "feature_stopped" }, (payload) => {
        const feature = (payload as { feature?: string })?.feature;
        if (isActiveTabValue(feature)) {
          setActiveFeatures((prev) => {
            const next = new Set(prev);
            next.delete(feature);
            return next;
          });
        }
      });

    channel.subscribe((status) => {
      if (status === "SUBSCRIBED") {
        resolveJoin?.();
        resolveJoin = null;
        rejectJoin = null;
      } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
        const error = new Error(`Realtime subscription failed: ${status}`);
        rejectJoin?.(error);
        resolveJoin = null;
        rejectJoin = null;
        console.warn("실시간 구독 실패, 폴링으로 대체", error);
      }
    });

    const loadInitialData = async () => {
      try {
        await Promise.all([
          refreshParticipants(),
          refreshQuestions(),
          refreshMessages(),
          refreshPolls(),
        ]);
        // 설문이 로드된 후 응답 데이터 로드
        await refreshPollResponses();
      } catch (error) {
        console.error("초기 데이터 로드 실패:", error);
      }
    };

    const refreshInterval = setInterval(() => {
      refreshParticipants();
      refreshQuestions();
      refreshMessages();
      refreshPolls();
    }, 3000);

    loadInitialData();

    return () => {
      clearInterval(refreshInterval);
      supabase.removeChannel(channel);

      if (realtimeChannelRef.current === channel) {
        realtimeChannelRef.current = null;
      }

      if (realtimeJoinPromiseRef.current === joinPromise) {
        realtimeJoinPromiseRef.current = null;
      }

      resolveJoin = null;
      rejectJoin = null;
    };
  }, [room, currentUser?.id]);

  // 세션 복구
  useEffect(() => {
    if (sessionRestoreAttemptedRef.current || !authReady) {
      return;
    }

    const savedSession = readStoredSession();
    if (!savedSession) {
      sessionRestoreAttemptedRef.current = true;
      return;
    }

    const { roomId, displayName, lastTab, activeFeatures: storedFeatures } =
      savedSession;

    if (displayName) {
      setTeacherName(displayName);
    }

    if (lastTab) {
      setTab(lastTab);
    }

    const validStoredFeatures = Array.isArray(storedFeatures)
      ? storedFeatures.filter(isActiveTabValue)
      : [];
    if (validStoredFeatures.length > 0) {
      setActiveFeatures(new Set(validStoredFeatures));
    }

    if (!currentUser) {
      return; // 인증 완료 후 effect가 다시 실행됩니다.
    }

    sessionRestoreAttemptedRef.current = true;

    const restoreAsync = async () => {
      try {
        const { data: roomData, error: roomError } = await supabase
          .from("rooms")
          .select(
            "id, code, title, host_user_id, status, expires_at, deleted_at, active_feature"
          )
          .eq("id", roomId)
          .single();

        if (roomError || !roomData) {
          localStorage.removeItem(TEACHER_SESSION_KEY);
          console.error("세션 복구 중 방 정보 조회 실패:", roomError);
          return;
        }

        const nextFeatures = new Set<ActiveTab>(validStoredFeatures);
        if (isActiveTabValue(roomData.active_feature)) {
          nextFeatures.add(roomData.active_feature);
        }
        setActiveFeatures(nextFeatures);

        if (!lastTab && nextFeatures.size > 0) {
          const [firstFeature] = Array.from(nextFeatures);
          if (firstFeature) {
            setTab(firstFeature);
          }
        }

        setRoom(roomData as Room);
        setView("room");

        const joinUrl = `${window.location.origin}?code=${roomData.code}`;
        generateQrCode(joinUrl);
      } catch (restoreError) {
        console.error("세션 복구 실패:", restoreError);
        localStorage.removeItem(TEACHER_SESSION_KEY);
      }
    };

    restoreAsync();
  }, [authReady, currentUser, generateQrCode, readStoredSession]);

  const joinUrl = useMemo(() => {
    if (!room) return "";
    let url = `${window.location.origin}?code=${room.code}`;
    if (isAnonJoin) {
      url += `&anon=true&roles=${encodeURIComponent(customRoles.join(","))}`;
    }
    return url;
  }, [room, isAnonJoin, customRoles]);

  useEffect(() => {
    if (joinUrl) {
      generateQrCode(joinUrl);
    }
  }, [joinUrl, generateQrCode]);

  // 익명 참여자 자동 번호 부여 (호스트 권한)
  useEffect(() => {
    if (!room || !isAnonJoin || participants.length === 0) return;

    // 현재 설정된 역할 중 어떤 역할 문자열과 정확히 일치하는지 찾기
    const pendingParticipants = participants.filter(
      (p) => customRoles.includes(p.display_name)
    );

    if (pendingParticipants.length > 0) {
      // 가입 시간 순 정렬
      const sortedPending = [...pendingParticipants].sort((a, b) =>
        new Date(a.joined_at).getTime() - new Date(b.joined_at).getTime()
      );

      const inMemoryParticipants = [...participants];

      const assignNames = async () => {
        for (const p of sortedPending) {
          const role = p.display_name;
          let maxNumber = 0;
          const rolePrefix = `${role} `;

          for (const existing of inMemoryParticipants) {
            if (existing.display_name.startsWith(rolePrefix)) {
              const numStr = existing.display_name.slice(rolePrefix.length);
              const num = parseInt(numStr, 10);
              if (!isNaN(num) && num > maxNumber) {
                maxNumber = num;
              }
            }
          }

          const newName = `${role} ${maxNumber + 1}`;

          // inMemory 업데이트로 다음 대기자 처리 시 중복 방지
          const idx = inMemoryParticipants.findIndex(x => x.id === p.id);
          if (idx !== -1) {
            inMemoryParticipants[idx] = { ...p, display_name: newName };
          }

          const { error } = await supabase
            .from("room_participants")
            .update({ display_name: newName })
            .eq("id", p.id);

          if (error) {
            console.error("참여자 닉네임 자동 부여 실패:", error);
          }
        }
      };

      assignNames();
    }
  }, [participants, room, isAnonJoin, customRoles]);

  const teacherUrl = "/?role=teacher";
  const newTabLink = (
    <a
      href={teacherUrl}
      target="_blank"
      rel="noopener noreferrer"
      title="새 탭에서 열기"
      className="new-tab-link"
    >
      <ExternalLinkIcon size={15} />
    </a>
  );

  if (view === "create") {
    return (
      <div className="classlive-sidebar" style={{ position: 'relative' }}>
        {newTabLink}
        <div className="classlive-header">
          <div className="classlive-header-top">
            <strong>ClassLive 교사</strong>
            {/* SsamFlow 계정 칩(sf-login.js가 그림). 로그인 안 했으면 숨겨져 자리를 차지하지 않음 */}
            <span className="sf-chip-slot" data-sf-login-chip />
          </div>
          <span className="classlive-chip">교실 생성 또는 재입장</span>
        </div>
        <div className="classlive-scroll">
          {error && <div className="classlive-alert">{error}</div>}

          <form onSubmit={handleCreateRoom} className="classlive-card">
            <div className="classlive-heading">교실 생성</div>
            <p className="classlive-body">
              교실을 생성합니다. 최장 24시간 유지됩니다.
            </p>
            <div className="classlive-grid">
              <input
                className="classlive-input"
                placeholder="교사 이름"
                value={teacherName}
                onChange={(e) => setTeacherName(e.target.value)}
                required
                maxLength={20}
                disabled={loading}
              />
              <button
                className="classlive-button"
                type="submit"
                disabled={loading || !teacherName.trim() || !currentUser}
              >
                {loading ? "생성 중..." : "교실 생성"}
              </button>
            </div>
          </form>

          <form onSubmit={handleRejoinRoom} className="classlive-card">
            <div className="classlive-heading">재입장</div>
            <p className="classlive-body">
              코드를 입력하여 기존 교실에 입장하세요.
            </p>
            <div className="classlive-grid">
              <input
                className="classlive-input"
                placeholder="6자리 교실 코드"
                value={rejoinCode}
                onChange={(e) => setRejoinCode(e.target.value)}
                required
                maxLength={6}
                disabled={loading}
              />
              <button
                className="classlive-button"
                type="submit"
                disabled={loading || !rejoinCode.trim() || !currentUser}
              >
                {loading ? "입장 중..." : "재입장"}
              </button>
            </div>
          </form>
        </div>
        <footer className="copyright-footer">
          <span>Copyright © 2025{new Date().getFullYear() > 2025 ? `-${new Date().getFullYear()}` : ''} SsamFlow. All rights reserved.</span>
          <div className="legal-links">
            <button type="button" onClick={() => setLegalModal("tos")}>
              이용약관
            </button>
            <button type="button" onClick={() => setLegalModal("privacy")}>
              개인정보처리방침
            </button>
          </div>
        </footer>

        <LegalModal
          isOpen={legalModal === "tos"}
          onClose={() => setLegalModal(null)}
          title="이용약관"
        >
          <TermsOfService />
        </LegalModal>

        <LegalModal
          isOpen={legalModal === "privacy"}
          onClose={() => setLegalModal(null)}
          title="개인정보처리방침"
        >
          <PrivacyPolicy />
        </LegalModal>
      </div>
    );
  }

  const tabs = [
    {
      id: "participants" as ActiveTab,
      label: "참여자",
      icon: <UsersIcon size={17} />,
      description: "참여자 목록 및 관리",
    },
    {
      id: "questions" as ActiveTab,
      label: "질문",
      icon: <HandIcon size={17} />,
      description: "실시간 질문 및 답변",
    },
    {
      id: "chat" as ActiveTab,
      label: "채팅",
      icon: <MessageIcon size={17} />,
      description: "채팅",
    },
    {
      id: "polls" as ActiveTab,
      label: "설문",
      icon: <ChartIcon size={17} />,
      description: "설문 조사",
    },
    {
      id: "tools" as ActiveTab,
      label: "도구",
      icon: <WrenchIcon size={17} />,
      description: "타이머 · 룰렛 · 주사위 · 칠판",
    },
  ];

  return (
    <div className="classlive-sidebar" style={{ position: 'relative' }}>
      {newTabLink}
      <div className="classlive-header classlive-header--room">
        <div className="classlive-header-top">
          <strong>{room?.title ?? "ClassLive"}</strong>
          <div className="classlive-header-actions">
            <span className="classlive-room-code">
              코드 {room?.code ?? ""}
            </span>
            {/* SsamFlow 계정 칩(sf-login.js가 그림). 로그인 안 했으면 숨겨져 자리를 차지하지 않음 */}
            <span className="sf-chip-slot" data-sf-login-chip />
          </div>
        </div>
        {overlayWindows !== null && (
          <div className={`overlay-target-row${overlayTargetId !== null ? " is-targeting-other" : ""}`}>
            <span className="overlay-target-label">오버레이 대상</span>
            <select
              className="overlay-target-select"
              value={overlayTargetId ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                const newId = val === "" ? null : Number(val);
                setOverlayTargetId(newId);
                postToExtension({
                  type: "classlive:set-overlay-target",
                  windowId: newId,
                });
              }}
            >
              <option value="">현재 창</option>
              {overlayWindows.map((w) => (
                <option key={w.windowId} value={w.windowId}>
                  {w.title.length > 28 ? w.title.slice(0, 27) + "…" : w.title}
                </option>
              ))}
            </select>
            {overlayTargetId !== null && (
              <span className="overlay-target-dot" />
            )}
          </div>
        )}
        <div className="classlive-tabs classlive-tabs--compact" role="tablist">
          {tabs.map((item) => (
            <button
              key={item.id}
              className={`classlive-tab classlive-tab-icon${tab === item.id ? " is-active" : ""
                }`}
              onClick={async () => {
                setTab(item.id);
                // 탭 변경 시 학생들에게 브로드캐스트하고 DB 업데이트
                if (item.id !== "participants" && activeFeatures.has(item.id)) {
                  await broadcastFeatureChange("feature_started", item.id);
                  if (room) {
                    await supabase.from("rooms").update({ active_feature: item.id }).eq("id", room.id);
                  }
                }
              }}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              title={item.description}
            >
              <span className="classlive-tab-emoji">{item.icon}</span>
              <span className="classlive-tab-label">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="classlive-scroll">
        {tab === "participants" && (
          <HostSharePanel
            code={room?.code ?? ""}
            joinUrl={joinUrl}
            qrDataUrl={qrDataUrl}
            onCopy={handleCopyLink}
            copyState={copyState}
            onShowQrModal={handleShowQrModal}
            participants={participants}
            room={room}
            isAnonJoin={isAnonJoin}
            setIsAnonJoin={setIsAnonJoin}
            customRoles={customRoles}
            setCustomRoles={setCustomRoles}
          />
        )}
        {tab === "questions" && (
          <FeaturePanel
            title="질문"
            description="학생들의 질문을 실시간으로 받고 답변할 수 있습니다."
            isActive={activeFeatures.has("questions")}
            onStart={() => handleStartFeature("questions")}
            onStop={() => handleStopFeature("questions")}
          >
            {activeFeatures.has("questions") && (
              <QuestionQueue
                roomId={room?.id ?? null}
                questions={questions}
                onPresentQuestion={openQuestionModal}
              />
            )}
          </FeaturePanel>
        )}
        {tab === "chat" && (
          <FeaturePanel
            title="채팅"
            description="학생들과 실시간으로 채팅할 수 있습니다."
            isActive={activeFeatures.has("chat")}
            onStart={() => handleStartFeature("chat")}
            onStop={() => handleStopFeature("chat")}
          >
            {activeFeatures.has("chat") && (
              <ChatPanel
                messages={messages}
                currentUser={currentUser}
                room={room}
              />
            )}
          </FeaturePanel>
        )}
        {tab === "polls" && (
          <FeaturePanel
            title="설문 조사"
            description="학생들에게 설문을 진행할 수 있습니다."
            isActive={activeFeatures.has("polls")}
            onStart={() => handleStartFeature("polls")}
            onStop={() => handleStopFeature("polls")}
          >
            {activeFeatures.has("polls") && (
              <PollDashboard
                polls={polls}
                pollResponses={pollResponses}
                room={room}
                participants={participants}
                onPresentPollResults={handlePresentPollResults}
                presentedPollId={presentedPollId}
                onClosePresentation={closeModal}
              />
            )}
          </FeaturePanel>
        )}
        {tab === "tools" && (
          <ToolsPanel
            participants={participants}
            onSendToolEvent={sendToolEvent}
            controlRequest={toolControlRequest}
            onConsumeControlRequest={handleConsumeToolControl}
          />
        )}
      </div>
      <div className="classlive-footer">
        <div className="classlive-body">{teacherName} (교사)</div>
        <button
          className="classlive-tab"
          type="button"
          onClick={() => setShowExitModal(true)}
        >
          교실 나가기
        </button>

      </div>
      <div className="copyright-footer">
        Copyright © 2025{new Date().getFullYear() > 2025 ? `-${new Date().getFullYear()}` : ''} SsamFlow. All rights reserved.
      </div>

      {showExitModal && (
        <div className="classlive-exit-modal-overlay">
          <div className="classlive-exit-modal">
            <h2>교실 종료 및 기록 다운로드</h2>
            <p>교실을 닫으면 모든 데이터가 영구적으로 삭제돼요. 보관할 기록을 골라 다운로드하세요.</p>

            <div className="exit-modal-options">
              <label className="exit-modal-option">
                <input
                  type="checkbox"
                  checked={exportSelections.questions}
                  onChange={(e) => setExportSelections({ ...exportSelections, questions: e.target.checked })}
                />
                질문 기록 다운로드 ({questions.length}개)
              </label>
              <label className="exit-modal-option">
                <input
                  type="checkbox"
                  checked={exportSelections.chat}
                  onChange={(e) => setExportSelections({ ...exportSelections, chat: e.target.checked })}
                />
                채팅 기록 다운로드 ({messages.length}개)
              </label>
              <label className="exit-modal-option">
                <input
                  type="checkbox"
                  checked={exportSelections.polls}
                  onChange={(e) => setExportSelections({ ...exportSelections, polls: e.target.checked })}
                />
                설문 조사 결과 다운로드 ({polls.length}개)
              </label>
            </div>

            <div className="exit-modal-actions">
              <button
                className="exit-btn-download"
                onClick={handleDownloadRecords}
                disabled={!exportSelections.questions && !exportSelections.chat && !exportSelections.polls}
              >
                선택 항목 추출 및 다운로드
              </button>
              <button className="exit-btn-danger" onClick={executeExitRoom}>
                데이터 완전히 삭제 후 수업 종료
              </button>
              <button className="exit-btn-cancel" onClick={() => setShowExitModal(false)}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// 기능 패널 컴포넌트
function FeaturePanel({
  title,
  description,
  isActive,
  onStart,
  onStop,
  children,
}: {
  title: string;
  description: string;
  isActive: boolean;
  onStart: () => void;
  onStop: () => void;
  children?: React.ReactNode;
}) {
  if (!isActive) {
    return (
      <section className="classlive-card">
        <div className="classlive-heading">{title}</div>
        <p className="classlive-body">{description}</p>
        <button className="classlive-button" onClick={onStart}>
          시작하기
        </button>
      </section>
    );
  }

  return (
    <section className="classlive-card">
      <div className="classlive-heading-with-action">
        <div className="classlive-heading">{title}</div>
        <button
          className="classlive-button classlive-button-danger"
          onClick={onStop}
        >
          중지
        </button>
      </div>
      {children}
    </section>
  );
}

function HostSharePanel({
  code,
  joinUrl,
  qrDataUrl,
  onCopy,
  copyState,
  onShowQrModal,
  participants,
  room,
  isAnonJoin,
  setIsAnonJoin,
  customRoles,
  setCustomRoles,
}: {
  code: string;
  joinUrl: string;
  qrDataUrl: string | null;
  onCopy: (url: string) => void;
  copyState: "idle" | "copied" | "error";
  onShowQrModal: (qrDataUrl: string, code: string) => void;
  participants: Participant[];
  room: Room | null;
  isAnonJoin: boolean;
  setIsAnonJoin: (val: boolean) => void;
  customRoles: string[];
  setCustomRoles: (roles: string[]) => void;
}) {
  const [newRole, setNewRole] = useState("");

  const handleAddRole = () => {
    const trimmed = newRole.trim();
    if (trimmed && !customRoles.includes(trimmed)) {
      setCustomRoles([...customRoles, trimmed]);
    }
    setNewRole("");
  };

  const handleRemoveRole = (r: string) => {
    setCustomRoles(customRoles.filter((role) => role !== r));
  };
  return (
    <>
      <section
        className="classlive-card classlive-share"
        aria-label="방 공유 정보"
      >
        <div className="classlive-share-header">
          <div>
            <div className="classlive-heading">참여 코드 공유</div>
            <p className="classlive-body">
              QR 또는 링크를 공유하여 학생이 바로 입장할 수 있습니다.
            </p>
          </div>
          <span className="classlive-share-code">{code}</span>
        </div>
        <div className="classlive-share-body">
          <div className="classlive-share-qr" aria-hidden={!qrDataUrl}>
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt={`참여 코드 ${code} QR`}
                onClick={() => onShowQrModal(qrDataUrl, code)}
                style={{ cursor: "pointer" }}
              />
            ) : (
              <span className="classlive-body">QR 생성 중...</span>
            )}
          </div>
          <div className="classlive-share-actions">
            <label className="classlive-body" htmlFor="classlive-join-url">
              참여 링크
            </label>
            <input
              id="classlive-join-url"
              className="classlive-input"
              value={joinUrl}
              readOnly
              onFocus={(event) => event.currentTarget.select()}
            />
            <div className="classlive-tabs">
              <button
                className="classlive-button"
                type="button"
                onClick={() => onCopy(joinUrl)}
              >
                링크 복사
              </button>
              <span className="classlive-body">
                {copyState === "copied" && "복사 완료!"}
                {copyState === "error" && "복사에 실패했습니다."}
              </span>
            </div>

            <div style={{ marginTop: "16px", padding: "12px", border: "1px solid #ddd", borderRadius: "8px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: "bold" }}>
                <input
                  type="checkbox"
                  checked={isAnonJoin}
                  onChange={(e) => setIsAnonJoin(e.target.checked)}
                />
                익명 참여 (가명 자동 생성)
              </label>
              {isAnonJoin && (
                <div style={{ marginTop: "12px" }}>
                  <p className="classlive-body" style={{ marginBottom: "8px" }}>학생들이 선택할 수 있는 역할을 설정하세요.</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
                    {customRoles.map((role) => (
                      <span key={role} style={{ background: "#eee", padding: "4px 8px", borderRadius: "4px", fontSize: "14px", display: "flex", alignItems: "center", gap: "4px" }}>
                        {role}
                        <button type="button" onClick={() => handleRemoveRole(role)} style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: "0 4px" }}>×</button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input
                      className="classlive-input"
                      placeholder="새 역할 입력"
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddRole())}
                      style={{ flex: 1, minWidth: "120px", height: "40px", padding: "0 12px" }}
                    />
                    <button
                      type="button"
                      className="classlive-button"
                      onClick={handleAddRole}
                      style={{ flex: "0 0 auto", width: "auto", height: "40px", padding: "0 16px", margin: 0, whiteSpace: "nowrap" }}
                    >
                      추가
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <ParticipantManager participants={participants} room={room} />
    </>
  );
}

// 질문 큐 컴포넌트
function QuestionQueue({
  roomId,
  questions,
  onPresentQuestion,
}: {
  roomId: string | null;
  questions: Question[];
  onPresentQuestion: (question: Question, ordered: Question[]) => void;
}) {
  const [deletingQuestionId, setDeletingQuestionId] = useState<string | null>(
    null
  );
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const sortedQuestions = useMemo(
    () =>
      [...questions].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ),
    [questions]
  );

  const handleDeleteQuestion = useCallback(async (questionId: string) => {
    if (!confirm("이 질문을 삭제하시겠습니까?")) {
      return;
    }
    setDeletingQuestionId(questionId);
    try {
      const { error } = await supabase
        .from("room_questions")
        .delete()
        .eq("id", questionId);

      if (error) {
        console.error("질문 삭제 실패:", error);
        alert("질문 삭제 중 오류가 발생했습니다.");
      }
    } finally {
      setDeletingQuestionId(null);
    }
  }, []);

  const handleDeleteAllQuestions = useCallback(async () => {
    if (!roomId) {
      console.warn("방 ID가 없어 질문 전체 삭제를 건너뜁니다.");
      return;
    }
    if (!confirm("등록된 모든 질문을 삭제하시겠습니까?")) {
      return;
    }
    setBulkDeleting(true);
    try {
      const { error } = await supabase
        .from("room_questions")
        .delete()
        .eq("room_id", roomId);

      if (error) {
        console.error("질문 전체 삭제 실패:", error);
        alert("질문 전체 삭제 중 오류가 발생했습니다.");
      }
    } finally {
      setBulkDeleting(false);
    }
  }, [roomId]);

  return (
    <div>
      <div className="classlive-question-actions">
        <button
          className="classlive-question-action classlive-question-action--danger"
          type="button"
          onClick={handleDeleteAllQuestions}
          disabled={bulkDeleting || !roomId || sortedQuestions.length === 0}
        >
          {bulkDeleting ? "전체 삭제 중..." : "질문 전체 삭제"}
        </button>
      </div>
      {sortedQuestions.length === 0 ? (
        <div className="classlive-empty">등록된 질문이 없습니다.</div>
      ) : (
        <div className="classlive-list">
          {sortedQuestions.map((question) => (
            <article
              key={question.id}
              className="classlive-card classlive-question-card"
            >
              <div className="classlive-question-header">
                <div className="classlive-heading">{question.author_name}</div>
                <button
                  type="button"
                  className="classlive-question-action"
                  onClick={() => handleDeleteQuestion(question.id)}
                  disabled={deletingQuestionId === question.id}
                >
                  {deletingQuestionId === question.id ? "삭제 중..." : "삭제"}
                </button>
              </div>
              <div
                className="classlive-body classlive-clickable-question"
                onClick={() => {
                  const ordered = sortedQuestions.filter(
                    (q) => q.status === "open"
                  );
                  onPresentQuestion(question, ordered);
                }}
              >
                {question.body}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

// 채팅 패널 컴포넌트
function ChatPanel({
  messages,
  currentUser,
  room,
}: {
  messages: ChatMessage[];
  currentUser: any;
  room: Room | null;
}) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const publicMessages = useMemo(() => {
    return messages.filter((message) => message.visibility === "public");
  }, [messages]);

  // 자동 스크롤
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [publicMessages, scrollToBottom]);

  const handleSendMessage = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!room || !currentUser || !body.trim()) {
        return;
      }

      setSending(true);
      try {
        const { error } = await supabase.from("room_messages").insert({
          room_id: room.id,
          sender_id: currentUser.id,
          sender_name: "교사",
          body: body.trim(),
          visibility: "public",
        });

        if (error) throw error;

        // 메시지 전송 완료 - 실시간 구독이 자동으로 감지함

        setBody("");
      } catch (error) {
        console.error("메시지 전송 실패:", error);
      } finally {
        setSending(false);
      }
    },
    [body, currentUser, room]
  );

  return (
    <div className="classlive-chat">
      <div className="classlive-messages">
        {publicMessages.length === 0 ? (
          <div className="classlive-empty">아직 메시지가 없습니다.</div>
        ) : (
          publicMessages.map((message) => {
            const isOwnMessage = message.sender_id === currentUser?.id;
            return (
              <div
                key={message.id}
                className={`classlive-message-bubble ${isOwnMessage ? "own-message" : "other-message"
                  }`}
              >
                <div className="classlive-message-content">
                  <div className="classlive-message-header">
                    <span className="classlive-message-author">
                      {message.sender_name}
                    </span>
                    <span className="classlive-message-time">
                      {new Date(message.created_at).toLocaleTimeString(
                        "ko-KR",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                    </span>
                  </div>
                  <div className="classlive-message-body">
                    {renderTextWithLinks(message.body ?? "", message.id)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="classlive-message-form">
        <input
          className="classlive-input"
          placeholder="메시지를 입력하세요..."
          value={body}
          onChange={(event) => setBody(event.target.value)}
          disabled={sending}
        />
        <button
          className="classlive-button"
          type="submit"
          disabled={sending || !body.trim()}
        >
          {sending ? (
            "전송 중..."
          ) : (
            <>
              <SendIcon size={14} /> 전송
            </>
          )}
        </button>
      </form>
    </div>
  );
}

// 설문 대시보드 컴포넌트
type WordSummary = {
  word: string;
  count: number;
};

const extractWordFromAnswer = (answer: unknown): string | null => {
  if (answer === null || answer === undefined) {
    return null;
  }

  let working: unknown = answer;

  if (typeof working === "string") {
    const trimmed = working.trim();
    if (!trimmed) {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed === "string") {
        working = parsed;
      } else if (Array.isArray(parsed)) {
        working = parsed[0] ?? "";
      } else {
        working = trimmed;
      }
    } catch {
      working = trimmed;
    }
  } else if (Array.isArray(working)) {
    working = working[0] ?? "";
  }

  const value =
    typeof working === "string" ? working.trim() : String(working ?? "").trim();

  if (!value) {
    return null;
  }

  const [firstToken] = value.split(/\s+/);
  return firstToken || null;
};

const aggregateWordResponses = (responses: PollResponse[]): WordSummary[] => {
  const counts = new Map<string, WordSummary>();

  responses.forEach((response) => {
    const word = extractWordFromAnswer(response.answer);
    if (!word) {
      return;
    }

    const key = word.toLowerCase();
    const existing = counts.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(key, { word, count: 1 });
    }
  });

  return Array.from(counts.values()).sort((a, b) => {
    if (b.count !== a.count) {
      return b.count - a.count;
    }
    return a.word.localeCompare(b.word, "ko");
  });
};

function PollDashboard({
  polls,
  pollResponses,
  room,
  participants,
  onPresentPollResults,
  presentedPollId,
  onClosePresentation,
}: {
  polls: Poll[];
  pollResponses: Record<string, PollResponse[]>;
  room: Room | null;
  participants: Participant[];
  onPresentPollResults: (pollId: string) => void;
  presentedPollId: string | null;
  onClosePresentation: () => void;
}) {
  const [type, setType] = useState<
    "multiple_choice" | "checkbox" | "open_text" | "word_cloud"
  >("multiple_choice");
  const [prompt, setPrompt] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [includeOther, setIncludeOther] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [convertingPollId, setConvertingPollId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [startingPollId, setStartingPollId] = useState<string | null>(null);
  const [reopeningPollId, setReopeningPollId] = useState<string | null>(null);
  const [closingPollId, setClosingPollId] = useState<string | null>(null);
  const [expandedOpenTextPolls, setExpandedOpenTextPolls] = useState<Set<string>>(new Set());
  const [expandedOptionResponders, setExpandedOptionResponders] = useState<Set<string>>(new Set());
  const [deletingPollId, setDeletingPollId] = useState<string | null>(null);
  const [orderedPolls, setOrderedPolls] = useState<Poll[]>(() =>
    sortPollsByOrder(polls)
  );
  const [draggedPollId, setDraggedPollId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const reorderingRef = useRef(false);

  useEffect(() => {
    if (reorderingRef.current) {
      return;
    }
    setOrderedPolls(sortPollsByOrder(polls));
  }, [polls]);

  const participantLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    participants.forEach((participant) => {
      map.set(participant.id, participant.display_name);
    });
    return map;
  }, [participants]);

  const toggleOpenText = useCallback((pollId: string) => {
    setExpandedOpenTextPolls((current) => {
      const next = new Set(current);
      if (next.has(pollId)) {
        next.delete(pollId);
      } else {
        next.add(pollId);
      }
      return next;
    });
  }, []);

  const toggleOptionResponders = useCallback((pollId: string, optionId: string) => {
    setExpandedOptionResponders((current) => {
      const next = new Set(current);
      const key = `${pollId}:${optionId}`;
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleCreatePoll = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!prompt.trim()) {
        alert("설문 질문을 입력해주세요.");
        return;
      }
      if (!room) {
        alert("방 정보를 찾지 못했어요.");
        return;
      }

      if (isChoicePoll(type) && options.filter((option) => option.trim()).length < 2) {
        alert("객관식 설문은 선택지를 2개 이상 입력해주세요.");
        return;
      }

      setCreating(true);
      try {
        const nextOrderIndex = orderedPolls.reduce(
          (maxOrder, poll) => Math.max(maxOrder, poll.order_index ?? -1),
          -1
        ) + 1;
        const { data: pollData, error: pollError } = await supabase
          .from("room_polls")
          .insert({
            room_id: room.id,
            type,
            prompt: prompt.trim(),
            is_anonymous: isAnonymous,
            order_index: nextOrderIndex,
          })
          .select("*")
          .single();

        if (pollError) throw pollError;

        if (isChoicePoll(type) && pollData) {
          const validOptions = options.filter((opt) => opt.trim());
          const optionRows = validOptions.map((label, index) => ({
            poll_id: pollData.id,
            label: label.trim(),
            order_index: index,
          }));
          if (includeOther) {
            optionRows.push({
              poll_id: pollData.id,
              label: OTHER_OPTION_LABEL,
              order_index: optionRows.length,
            });
          }
          if (optionRows.length > 0) {
            const { error: optionsError } = await supabase
              .from("room_poll_options")
              .insert(optionRows);
            if (optionsError) throw optionsError;
          }
        }

        setPrompt("");
        setOptions(["", ""]);
        setIncludeOther(false);
        alert("설문을 만들었어요!");
      } catch (error) {
        console.error("설문 생성 실패:", error);
        alert(`설문을 만들지 못했어요.`);
      } finally {
        setCreating(false);
      }
    },
    [includeOther, isAnonymous, options, orderedPolls, prompt, room, type]
  );

  const handlePollDrop = useCallback(
    async (targetPollId: string) => {
      if (!room || !draggedPollId || draggedPollId === targetPollId || reordering) {
        return;
      }

      const currentOrder = orderedPolls;
      const sourceIndex = currentOrder.findIndex((poll) => poll.id === draggedPollId);
      const targetIndex = currentOrder.findIndex((poll) => poll.id === targetPollId);
      if (sourceIndex === -1 || targetIndex === -1) {
        return;
      }

      const nextOrder = [...currentOrder];
      const [movedPoll] = nextOrder.splice(sourceIndex, 1);
      const insertIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      nextOrder.splice(insertIndex, 0, movedPoll);
      setOrderedPolls(nextOrder);
      reorderingRef.current = true;
      setReordering(true);

      try {
        const results = await Promise.all(
          nextOrder.map((poll, index) =>
            supabase
              .from("room_polls")
              .update({ order_index: index })
              .eq("id", poll.id)
              .eq("room_id", room.id)
          )
        );
        const updateError = results.find((result) => result.error)?.error;
        if (updateError) {
          throw updateError;
        }
      } catch (error) {
        console.error("설문 순서 저장 실패:", error);
        setOrderedPolls(currentOrder);
        alert("설문 순서를 저장하지 못했어요. 데이터베이스 마이그레이션을 확인해주세요.");
      } finally {
        setDraggedPollId(null);
        reorderingRef.current = false;
        setReordering(false);
      }
    },
    [draggedPollId, orderedPolls, reordering, room]
  );

  const handleConvertPollType = useCallback(
    async (pollId: string, nextType: "multiple_choice" | "checkbox") => {
      setConvertingPollId(pollId);
      try {
        const { error } = await supabase
          .from("room_polls")
          .update({ type: nextType })
          .eq("id", pollId);
        if (error) throw error;
      } catch (error) {
        console.error("설문 유형 변경 실패:", error);
        alert("설문 유형을 바꾸지 못했어요.");
      } finally {
        setConvertingPollId(null);
      }
    },
    []
  );

  const handleClosePoll = useCallback(async (pollId: string) => {
    setClosingPollId(pollId);
    try {
      const closedAt = new Date().toISOString();
      const { error } = await supabase
        .from("room_polls")
        .update({ closed_at: closedAt })
        .eq("id", pollId);
      if (error) throw error;
    } catch (error) {
      console.error("설문 종료 실패:", error);
      alert("설문 종료 중 오류가 발생했습니다.");
    } finally {
      setClosingPollId(null);
    }
  }, []);

  const handleStartPoll = useCallback(async (pollId: string) => {
    setStartingPollId(pollId);
    try {
      const startedAt = new Date().toISOString();
      const { error } = await supabase
        .from("room_polls")
        .update({ started_at: startedAt, closed_at: null })
        .eq("id", pollId);
      if (error) throw error;
    } catch (error) {
      console.error("설문 시작 실패:", error);
      alert("설문 시작 중 오류가 발생했습니다.");
    } finally {
      setStartingPollId(null);
    }
  }, []);

  const handleReopenPoll = useCallback(async (pollId: string) => {
    if (!window.confirm("재설문을 시작하면 기존 응답이 삭제됩니다. 진행할까요?")) {
      return;
    }
    setReopeningPollId(pollId);
    try {
      const restartedAt = new Date().toISOString();
      const { error: deleteError } = await supabase
        .from("room_poll_responses")
        .delete()
        .eq("poll_id", pollId);
      if (deleteError) throw deleteError;

      const { error: updateError } = await supabase
        .from("room_polls")
        .update({ started_at: restartedAt, closed_at: null })
        .eq("id", pollId);
      if (updateError) throw updateError;
    } catch (error) {
      console.error("재설문 시작 실패:", error);
      alert("재설문 시작 중 오류가 발생했습니다.");
    } finally {
      setReopeningPollId(null);
    }
  }, []);

  const handleDeletePoll = useCallback(async (pollId: string) => {
    if (!window.confirm("이 설문을 삭제할까요? 모든 응답도 함께 삭제돼요.")) {
      return;
    }
    setDeletingPollId(pollId);
    try {
      const { error } = await supabase.from("room_polls").delete().eq("id", pollId);
      if (error) throw error;
    } catch (error) {
      console.error("설문 삭제 실패:", error);
      alert("설문을 삭제하지 못했어요.");
    } finally {
      setDeletingPollId(null);
    }
  }, []);

  return (
    <div className="classlive-grid">
      <form className="classlive-card" onSubmit={handleCreatePoll}>
        <div className="classlive-heading">설문 만들기</div>
        <select
          className="classlive-select"
          value={type}
          onChange={(event) =>
            setType(
              event.target.value as
              | "multiple_choice"
              | "checkbox"
              | "open_text"
              | "word_cloud"
            )
          }
        >
          <option value="multiple_choice">객관식 (단일 선택)</option>
          <option value="checkbox">체크박스 (복수 선택)</option>
          <option value="open_text">주관식</option>
          <option value="word_cloud">워드 클라우드</option>
        </select>
        <textarea
          className="classlive-textarea"
          placeholder="질문 내용을 입력하세요"
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          required
        />
        {type === "word_cloud" && (
          <p className="classlive-helper-text">
            학생들은 한 단어씩 응답합니다. 결과는 워드 클라우드로 크게 표시됩니다.
          </p>
        )}
        {isChoicePoll(type) ? (
          <div className="classlive-grid">
            {options.map((option, index) => (
              <div className="classlive-poll-option-editor" key={`option-${index}`}>
                <input
                  className="classlive-input"
                  value={option}
                  placeholder={`선택지 ${index + 1}`}
                  onChange={(event) =>
                    setOptions((current) => {
                      const clone = [...current];
                      clone[index] = event.target.value;
                      return clone;
                    })
                  }
                  required
                />
                <button
                  className="classlive-tab classlive-option-delete"
                  type="button"
                  aria-label={`선택지 ${index + 1} 삭제`}
                  title={options.length <= 2 ? "선택지를 2개 이상 만들어주세요" : undefined}
                  disabled={options.length <= 2}
                  onClick={() =>
                    setOptions((current) =>
                      current.length > 2
                        ? current.filter((_, optionIndex) => optionIndex !== index)
                        : current
                    )
                  }
                >
                  삭제
                </button>
              </div>
            ))}
            <button
              className="classlive-tab"
              type="button"
              onClick={() => setOptions((current) => [...current, ""])}
            >
              선택지 추가
            </button>
            <label className="classlive-body" htmlFor="include-other-toggle">
              <input
                id="include-other-toggle"
                type="checkbox"
                checked={includeOther}
                onChange={(event) => setIncludeOther(event.target.checked)}
              />{" "}
              기타 항목 추가 (자유 응답 입력 가능)
            </label>
          </div>
        ) : null}
        <label className="classlive-body" htmlFor="anonymous-toggle">
          <input
            id="anonymous-toggle"
            type="checkbox"
            checked={isAnonymous}
            onChange={(event) => setIsAnonymous(event.target.checked)}
          />{" "}
          익명 응답 허용
        </label>
        <button className="classlive-button" type="submit" disabled={creating}>
          {creating ? "생성 중..." : "설문 생성"}
        </button>
      </form>

      <div className="classlive-grid">
        {orderedPolls.length === 0 ? (
          <div className="classlive-empty">진행 중인 설문이 없습니다.</div>
        ) : (
          orderedPolls.map((poll) => {
            const responses = pollResponses[poll.id] ?? [];
            const totalResponses = responses.length;
            const isDraft = !poll.started_at;
            const isClosed = Boolean(poll.closed_at);
            const isActive = !isDraft && !isClosed;
            const statusLabel = isDraft
              ? "대기 중"
              : isActive
                ? "진행 중"
                : "종료됨";
            const wordSummary =
              poll.type === "word_cloud"
                ? aggregateWordResponses(responses)
                : [];

            return (
              <article
                key={poll.id}
                className={`classlive-card classlive-poll-card${draggedPollId === poll.id ? " is-dragging" : ""}`}
                draggable={!reordering}
                onDragStart={() => setDraggedPollId(poll.id)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => void handlePollDrop(poll.id)}
                onDragEnd={() => setDraggedPollId(null)}
              >
                <div className="classlive-poll-drag-handle" aria-label="드래그하여 설문 순서 변경">
                  ☰ 드래그하여 순서 변경
                </div>
                <header className="classlive-heading">{poll.prompt}</header>
                <div className="classlive-body">
                  {poll.type === "checkbox" && "복수 선택 · "}
                  {poll.is_anonymous ? "익명 응답" : "기명 응답"} · 총{" "}
                  {totalResponses}명 참여 · {statusLabel}
                </div>

                {isChoicePoll(poll.type) && poll.options && (
                  <div className="classlive-grid">
                    {poll.options.map((option) => {
                      const optionResponses = responses.filter((response) =>
                        answerMatchesOption(response.answer, option.id)
                      );

                      const percentage =
                        totalResponses > 0
                          ? Math.round((optionResponses.length / totalResponses) * 100)
                          : 0;

                      const responderNames = poll.is_anonymous
                        ? []
                        : optionResponses.map(
                          (response) =>
                            participantLabelMap.get(response.participant_id) ??
                            "알 수 없음"
                        );

                      const optionKey = `${poll.id}:${option.id}`;
                      const showResponders = expandedOptionResponders.has(optionKey);
                      const otherTexts = isOtherOption(option.label)
                        ? optionResponses
                          .map((response) => extractOtherText(response.answer))
                          .filter((text): text is string => Boolean(text))
                        : [];

                      return (
                        <div key={option.id} className="classlive-poll-option-result">
                          <div className="classlive-body">
                            {displayOptionLabel(option.label)} ({optionResponses.length}명, {percentage}%)
                          </div>
                          <div
                            className="classlive-poll-progress"
                            style={{ width: `${percentage}%` }}
                          />
                          {otherTexts.length > 0 && (
                            <div className="classlive-responders-list">
                              {otherTexts.map((text, textIndex) => (
                                <span
                                  key={`${option.id}-other-${textIndex}`}
                                  className="classlive-responder-chip"
                                >
                                  {text}
                                </span>
                              ))}
                            </div>
                          )}
                          {!poll.is_anonymous && responderNames.length > 0 && (
                            <div className="classlive-poll-responders">
                              <button
                                type="button"
                                className="classlive-responders-toggle"
                                onClick={() => toggleOptionResponders(poll.id, option.id)}
                              >
                                {showResponders
                                  ? "응답자 닫기"
                                  : `응답자 보기 (${responderNames.length}명)`}
                              </button>
                              {showResponders && (
                                <div className="classlive-responders-list">
                                  {responderNames.map((name, responderIndex) => (
                                    <span
                                      key={`${option.id}-${responderIndex}`}
                                      className="classlive-responder-chip"
                                    >
                                      {name}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {poll.type === "open_text" && (
                  <div className="classlive-grid">
                    {(expandedOpenTextPolls.has(poll.id)
                      ? responses
                      : responses.slice(0, 5)
                    ).map((response) => {
                      const authorLabel = poll.is_anonymous
                        ? "익명"
                        : participantLabelMap.get(response.participant_id) ?? "알 수 없음";
                      const displayAnswer = stringifyPollAnswer(response.answer) || "(응답 없음)";

                      return (
                        <div key={response.id} className="classlive-body">
                          <strong>{authorLabel}: </strong>"{displayAnswer}"
                        </div>
                      );
                    })}
                    {responses.length > 5 && (
                      <button
                        className="classlive-show-more"
                        type="button"
                        onClick={() => toggleOpenText(poll.id)}
                      >
                        {expandedOpenTextPolls.has(poll.id)
                          ? "응답 접기"
                          : `...외 ${responses.length - 5}개 응답 보기`}
                      </button>
                    )}
                  </div>
                )}

                {poll.type === "word_cloud" && (
                  <div className="classlive-word-cloud-preview">
                    {wordSummary.length === 0 ? (
                      <div className="classlive-body">아직 단어 응답이 없어요.</div>
                    ) : (
                      wordSummary.slice(0, 12).map(({ word, count }) => (
                        <span key={word} className="classlive-word-chip">
                          {word}
                          <span className="classlive-word-count">{count}</span>
                        </span>
                      ))
                    )}
                  </div>
                )}

                <div className="classlive-tabs">
                  {presentedPollId === poll.id ? (
                    <button
                      className="classlive-tab classlive-tab-presenting"
                      type="button"
                      onClick={onClosePresentation}
                      title="메인 화면에 실시간으로 표시 중입니다"
                    >
                      <span className="presenting-dot" aria-hidden="true" />
                      화면에서 닫기
                    </button>
                  ) : (
                    <button
                      className="classlive-tab"
                      type="button"
                      onClick={() => onPresentPollResults(poll.id)}
                    >
                      결과 보기
                    </button>
                  )}
                  {isDraft && (
                    <button
                      className="classlive-tab"
                      type="button"
                      onClick={() => handleStartPoll(poll.id)}
                      disabled={startingPollId === poll.id || reopeningPollId === poll.id}
                    >
                      {startingPollId === poll.id ? "시작 중..." : "설문 시작"}
                    </button>
                  )}
                  {isDraft && isChoicePoll(poll.type) && (
                    <button
                      className="classlive-tab"
                      type="button"
                      onClick={() =>
                        handleConvertPollType(
                          poll.id,
                          poll.type === "checkbox" ? "multiple_choice" : "checkbox"
                        )
                      }
                      disabled={convertingPollId === poll.id}
                    >
                      {convertingPollId === poll.id
                        ? "변경 중..."
                        : poll.type === "checkbox"
                          ? "단일 선택으로 전환"
                          : "복수 선택으로 전환"}
                    </button>
                  )}
                  {isActive && (
                    <button
                      className="classlive-tab"
                      type="button"
                      onClick={() => handleClosePoll(poll.id)}
                      disabled={closingPollId === poll.id}
                    >
                      {closingPollId === poll.id ? "종료 중..." : "설문 종료"}
                    </button>
                  )}
                  {isClosed && (
                    <button
                      className="classlive-tab"
                      type="button"
                      onClick={() => handleReopenPoll(poll.id)}
                      disabled={reopeningPollId === poll.id || startingPollId === poll.id}
                    >
                      {reopeningPollId === poll.id
                        ? "재설문 준비 중..."
                        : "재설문"}
                    </button>
                  )}
                  <button
                    className="classlive-tab classlive-danger"
                    type="button"
                    onClick={() => handleDeletePoll(poll.id)}
                    disabled={deletingPollId === poll.id}
                  >
                    {deletingPollId === poll.id ? "삭제 중..." : "설문 삭제"}
                  </button>
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}

type DiceConfig = {
  id: string;
  sides: number;
  color: string;
};

type DiceResult = DiceConfig & { value: number };

function ToolsPanel({
  participants,
  onSendToolEvent,
  controlRequest,
  onConsumeControlRequest,
}: {
  participants: Participant[];
  onSendToolEvent: (event: ToolEventPayload) => void;
  controlRequest: ToolControlRequest | null;
  onConsumeControlRequest?: () => void;
}) {
  const [timerMinutes, setTimerMinutes] = useState(1);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerEndTime, setTimerEndTime] = useState<number | null>(null);
  const [timerStartTime, setTimerStartTime] = useState<number | null>(null);
  const [timerRemaining, setTimerRemaining] = useState(0);
  const timerIntervalRef = useRef<number | null>(null);
  const timerCompletedRef = useRef(false);
  const lastControlRequestId = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const ensureAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext();
    } else if (audioCtxRef.current.state === "suspended") {
      void audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const playTone = useCallback(
    (frequency: number, durationMs = 200, volume = 0.2) => {
      try {
        const ctx = ensureAudioContext();
        if (!ctx) return;
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.type = "sine";
        oscillator.frequency.value = frequency;
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        gain.gain.setValueAtTime(volume, ctx.currentTime);
        oscillator.start();
        gain.gain.exponentialRampToValueAtTime(
          0.001,
          ctx.currentTime + durationMs / 1000
        );
        oscillator.stop(ctx.currentTime + durationMs / 1000);
      } catch (error) {
        console.warn("사운드 재생 실패:", error);
      }
    },
    [ensureAudioContext]
  );

  const clearTimerInterval = useCallback(() => {
    if (timerIntervalRef.current !== null) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  useEffect(() => () => clearTimerInterval(), [clearTimerInterval]);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = Math.max(0, seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${mins}:${secs}`;
  }, []);

  const resetTimerState = useCallback(() => {
    clearTimerInterval();
    setTimerEndTime(null);
    setTimerStartTime(null);
    setTimerRemaining(0);
    timerCompletedRef.current = false;
  }, [clearTimerInterval]);

  const handleStartTimer = useCallback(() => {
    const totalSeconds = timerMinutes * 60 + timerSeconds;
    if (totalSeconds <= 0) {
      alert("1초 이상으로 시간을 설정해주세요.");
      return;
    }

    const startTime = Date.now();
    const endTime = startTime + totalSeconds * 1000;
    timerCompletedRef.current = false;
    setTimerStartTime(startTime);
    setTimerEndTime(endTime);
    setTimerRemaining(totalSeconds);
    playTone(1040, 200, 0.3);
    clearTimerInterval();

    timerIntervalRef.current = window.setInterval(() => {
      const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
      setTimerRemaining(remaining);
      if (remaining <= 0 && !timerCompletedRef.current) {
        timerCompletedRef.current = true;
        clearTimerInterval();
        setTimerEndTime(null);
        setTimerStartTime(null);
        playTone(620, 600, 0.35);
        onSendToolEvent({ tool: "timer", action: "finish" });
      }
    }, 250);
  }, [clearTimerInterval, onSendToolEvent, playTone, timerMinutes, timerSeconds]);

  const handleStopTimer = useCallback(() => {
    resetTimerState();
    onSendToolEvent({ tool: "timer", action: "hide" });
  }, [onSendToolEvent, resetTimerState]);

  const handleShowTimerOnScreen = useCallback(() => {
    if (!timerEndTime || !timerStartTime) {
      alert("진행 중인 타이머가 없어요.");
      return;
    }
    onSendToolEvent({
      tool: "timer",
      action: "show",
      endTime: timerEndTime,
      durationMs: timerEndTime - timerStartTime,
      startedAt: timerStartTime,
    });
  }, [onSendToolEvent, timerEndTime, timerStartTime]);

  const [rouletteEntries, setRouletteEntries] = useState("");
  const [rouletteResult, setRouletteResult] = useState<string | null>(null);
  const [rouletteSpinning, setRouletteSpinning] = useState(false);
  const rouletteResultTimeoutRef = useRef<number | null>(null);
  const [removeWinnerAfterSpin, setRemoveWinnerAfterSpin] = useState(false);

  useEffect(() => () => {
    if (rouletteResultTimeoutRef.current !== null) {
      window.clearTimeout(rouletteResultTimeoutRef.current);
    }
  }, []);

  const getRouletteSpinDuration = useCallback((count: number) => {
    return Math.min(6000, 2500 + count * 120);
  }, []);

  const parseRouletteEntries = useCallback(() => {
    return rouletteEntries
      .split(/\n|,/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }, [rouletteEntries]);

  const handleLoadParticipants = useCallback(() => {
    if (!participants.length) {
      alert("참여자가 없어요.");
      return;
    }
    setRouletteEntries(
      Array.from(new Set(participants.map((p) => p.display_name))).join("\n")
    );
  }, [participants]);

  const handleSpinRoulette = useCallback(() => {
    const items = parseRouletteEntries();
    if (items.length < 2) {
      alert("항목을 2개 이상 넣어주세요.");
      return;
    }
    setRouletteSpinning(true);
    setRouletteResult(null);
    const winnerIndex = Math.floor(Math.random() * items.length);
    if (rouletteResultTimeoutRef.current !== null) {
      window.clearTimeout(rouletteResultTimeoutRef.current);
    }
    const duration = getRouletteSpinDuration(items.length);
    rouletteResultTimeoutRef.current = window.setTimeout(() => {
      setRouletteSpinning(false);
      setRouletteResult(items[winnerIndex]);
      if (removeWinnerAfterSpin) {
        const updated = items.filter((_, idx) => idx !== winnerIndex);
        setRouletteEntries(updated.join("\n"));
      }
      rouletteResultTimeoutRef.current = null;
    }, duration);
    onSendToolEvent({
      tool: "roulette",
      action: "spin",
      items,
      winnerIndex,
    });
  }, [getRouletteSpinDuration, onSendToolEvent, parseRouletteEntries]);

  const createId = useCallback(
    () =>
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2),
    []
  );

  const diceOptions = [4, 6, 8, 10, 12, 20];
  const [diceConfigs, setDiceConfigs] = useState<DiceConfig[]>([
    { id: createId(), sides: 6, color: "#2563eb" },
  ]);
  const [diceResults, setDiceResults] = useState<DiceResult[]>([]);

  const handleAddDie = useCallback(() => {
    const palette = [
      "#f87171",
      "#34d399",
      "#60a5fa",
      "#a78bfa",
      "#facc15",
      "#fb923c",
    ];
    const color = palette[Math.floor(Math.random() * palette.length)];
    setDiceConfigs((current) => [
      ...current,
      { id: createId(), sides: 6, color },
    ]);
  }, [createId]);

  const handleUpdateDie = useCallback(
    (id: string, key: keyof DiceConfig, value: number | string) => {
      setDiceConfigs((current) =>
        current.map((die) => (die.id === id ? { ...die, [key]: value } : die))
      );
    },
    []
  );

  const handleRemoveDie = useCallback((id: string) => {
    setDiceConfigs((current) => current.filter((die) => die.id !== id));
  }, []);

  const handleRollDice = useCallback(() => {
    if (diceConfigs.length === 0) {
      alert("주사위를 추가해주세요.");
      return;
    }
    const results = diceConfigs.map((die) => ({
      ...die,
      value: Math.floor(Math.random() * die.sides) + 1,
    }));
    setDiceResults(results);
    onSendToolEvent({
      tool: "dice",
      action: "roll",
      dice: results.map(({ color, sides, value }) => ({ color, sides, value })),
    });
  }, [diceConfigs, onSendToolEvent]);

  const [chalkboardText, setChalkboardText] = useState("");
  const [chalkboardPreview, setChalkboardPreview] = useState<string | null>(null);
  const [chalkboardFontSize, setChalkboardFontSize] = useState(72);

  const handlePostChalkboard = useCallback(() => {
    const trimmed = chalkboardText.trim();
    if (!trimmed) {
      alert("내용을 입력해주세요.");
      return;
    }
    setChalkboardPreview(trimmed);
    onSendToolEvent({
      tool: "chalkboard",
      action: "display",
      content: trimmed,
      fontSize: chalkboardFontSize,
    });
  }, [chalkboardFontSize, chalkboardText, onSendToolEvent]);

  const handleClearChalkboard = useCallback(() => {
    setChalkboardPreview(null);
    onSendToolEvent({ tool: "chalkboard", action: "clear" });
  }, [onSendToolEvent]);

  useEffect(() => {
    if (!controlRequest || controlRequest.id === lastControlRequestId.current) {
      return;
    }
    lastControlRequestId.current = controlRequest.id;
    switch (controlRequest.command.tool) {
      case "timer":
        if (controlRequest.command.action === "reset") {
          handleStopTimer();
        } else if (controlRequest.command.action === "restart") {
          handleStartTimer();
        }
        break;
      case "roulette":
        handleSpinRoulette();
        break;
      case "dice":
        handleRollDice();
        break;
      default:
        break;
    }
    onConsumeControlRequest?.();
  }, [
    controlRequest,
    handleRollDice,
    handleSpinRoulette,
    handleStartTimer,
    handleStopTimer,
    onConsumeControlRequest,
  ]);

  return (
    <div className="tools-panel">
      <section className="tool-card">
        <h3>
          <TimerIcon size={18} /> 타이머
        </h3>
        <p className="tool-description">
          시간을 설정하고 시작해보세요. 타이머 영역을 클릭하면 메인 화면에
          표시됩니다.
        </p>
        <div className="tool-field-grid">
          <label className="tool-field">
            분
            <input
              type="number"
              min={0}
              max={180}
              value={timerMinutes}
              onChange={(event) =>
                setTimerMinutes(
                  Math.min(180, Math.max(0, Number(event.target.value)))
                )
              }
              className="tool-number-input"
              disabled={Boolean(timerEndTime)}
            />
          </label>
          <label className="tool-field">
            초
            <input
              type="number"
              min={0}
              max={59}
              value={timerSeconds}
              onChange={(event) =>
                setTimerSeconds(
                  Math.min(59, Math.max(0, Number(event.target.value)))
                )
              }
              className="tool-number-input"
              disabled={Boolean(timerEndTime)}
            />
          </label>
        </div>
        <div className="tool-actions">
          <button
            type="button"
            className="classlive-button"
            onClick={handleStartTimer}
            disabled={Boolean(timerEndTime)}
          >
            타이머 시작
          </button>
          <button
            type="button"
            className="classlive-tab"
            onClick={handleStopTimer}
            disabled={!timerEndTime}
          >
            타이머 정지
          </button>
          <button
            type="button"
            className="classlive-tab"
            onClick={handleShowTimerOnScreen}
            disabled={!timerEndTime}
          >
            메인 화면 표시
          </button>
        </div>
        <div
          className={`tool-timer-display${timerEndTime ? " is-active" : ""}`}
          onClick={timerEndTime ? handleShowTimerOnScreen : undefined}
          role={timerEndTime ? "button" : undefined}
          tabIndex={timerEndTime ? 0 : -1}
          onKeyDown={(event) => {
            if (!timerEndTime) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleShowTimerOnScreen();
            }
          }}
        >
          {timerEndTime ? formatTime(timerRemaining) : "00:00"}
        </div>
      </section>

      <section className="tool-card">
        <h3>
          <TargetIcon size={18} /> 룰렛
        </h3>
        <p className="tool-description">
          참여자 목록을 불러오거나 직접 항목을 입력해 룰렛을 돌려보세요.
        </p>
        <textarea
          className="tool-textarea"
          placeholder="각 항목을 줄바꿈으로 구분하세요"
          value={rouletteEntries}
          onChange={(event) => setRouletteEntries(event.target.value)}
        />
        <div className="tool-actions">
          <button
            type="button"
            className="classlive-tab"
            onClick={handleLoadParticipants}
          >
            참여자 불러오기
          </button>
          <button
            type="button"
            className="classlive-button"
            onClick={handleSpinRoulette}
            disabled={rouletteSpinning}
          >
            {rouletteSpinning ? "돌리는 중..." : "룰렛 돌리기"}
          </button>
        </div>
        <label className="tool-field" style={{ alignItems: "center", gap: "8px" }}>
          <input
            type="checkbox"
            checked={removeWinnerAfterSpin}
            onChange={(event) => setRemoveWinnerAfterSpin(event.target.checked)}
          />
          당첨 항목을 다음 회차에서 제거
        </label>
        {rouletteResult && (
          <div className="tool-result-banner">
            <SparklesIcon size={17} /> {rouletteResult} 선정!
          </div>
        )}
      </section>

      <section className="tool-card">
        <h3>
          <DiceIcon size={18} /> 주사위
        </h3>
        <p className="tool-description">
          다양한 주사위를 추가하고 굴려보세요. 메인 화면에도 결과가 표시됩니다.
        </p>
        <div className="tool-dice-grid">
          {diceConfigs.map((die) => (
            <div key={die.id} className="tool-dice-card">
              <div className="tool-field-grid">
                <label className="tool-field">
                  면 수
                  <select
                    value={die.sides}
                    onChange={(event) =>
                      handleUpdateDie(die.id, "sides", Number(event.target.value))
                    }
                    className="tool-select"
                  >
                    {diceOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="tool-field">
                  색상
                  <input
                    type="color"
                    value={die.color}
                    onChange={(event) =>
                      handleUpdateDie(die.id, "color", event.target.value)
                    }
                    className="tool-color-input"
                  />
                </label>
              </div>
              {diceConfigs.length > 1 && (
                <button
                  type="button"
                  className="classlive-tab"
                  onClick={() => handleRemoveDie(die.id)}
                >
                  제거
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="tool-actions">
          <button type="button" className="classlive-tab" onClick={handleAddDie}>
            주사위 추가
          </button>
          <button
            type="button"
            className="classlive-button"
            onClick={handleRollDice}
          >
            굴리기
          </button>
        </div>
        {diceResults.length > 0 && (
          <div className="tool-dice-results">
            {diceResults.map((die) => (
              <div
                key={die.id}
                className="tool-dice-result"
                style={{ background: die.color }}
              >
                <span>{die.value}</span>
                <small>{die.sides}면체</small>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="tool-card">
        <h3>
          <PenLineIcon size={18} /> 칠판
        </h3>
        <p className="tool-description">
          공지나 노트를 적고 게시 버튼을 누르면 메인 화면에 표시됩니다.
        </p>
        <textarea
          className="tool-textarea"
          placeholder="칠판에 표시할 내용을 입력하세요"
          value={chalkboardText}
          onChange={(event) => setChalkboardText(event.target.value)}
        />
        <div className="tool-field-grid">
          <label className="tool-field">
            글꼴 크기
            <div className="tool-font-controls">
              <button
                type="button"
                className="classlive-tab"
                onClick={() =>
                  setChalkboardFontSize((size) => Math.max(24, size - 4))
                }
              >
                -
              </button>
              <div className="tool-font-value">{chalkboardFontSize}px</div>
              <button
                type="button"
                className="classlive-tab"
                onClick={() =>
                  setChalkboardFontSize((size) => Math.min(160, size + 4))
                }
              >
                +
              </button>
              <input
                type="number"
                min={24}
                max={160}
                value={chalkboardFontSize}
                onChange={(event) =>
                  setChalkboardFontSize(
                    Math.max(24, Math.min(160, Number(event.target.value)))
                  )
                }
                className="tool-font-input"
              />
            </div>
          </label>
        </div>
        <div className="tool-actions">
          <button type="button" className="classlive-button" onClick={handlePostChalkboard}>
            게시
          </button>
          <button type="button" className="classlive-tab" onClick={handleClearChalkboard}>
            메인 화면 지우기
          </button>
        </div>
        {chalkboardPreview && (
          <div className="tool-chalkboard-preview">
            <div className="tool-chalkboard-title">현재 게시된 내용</div>
            <p style={{ fontSize: chalkboardFontSize }}>{chalkboardPreview}</p>
          </div>
        )}
      </section>
    </div>
  );
}

// 참여자 관리 컴포넌트
function ParticipantManager({
  participants,
  room,
}: {
  participants: Participant[];
  room: Room | null;
}) {
  const [kickingId, setKickingId] = useState<string | null>(null);

  const handleKickParticipant = useCallback(async (participantId: string) => {
    if (!window.confirm("이 참여자를 내보낼까요?")) return;

    setKickingId(participantId);
    try {
      const { error } = await supabase
        .from("room_participants")
        .delete()
        .eq("id", participantId);

      if (error) {
        console.error("참여자 내보내기 실패:", error);
        alert("참여자를 내보내지 못했어요.");
      }
      // 성공 시, 실시간 구독이 자동으로 목록을 갱신합니다.
    } finally {
      setKickingId(null);
    }
  }, []);

  return (
    <div className="classlive-grid">
      <section className="classlive-card">
        <div className="classlive-heading">
          참여자 목록 ({participants.length}명)
        </div>
        {participants.length === 0 ? (
          <p className="classlive-body">아직 참여자가 없어요.</p>
        ) : (
          <div className="classlive-participants">
            {participants.map((participant) => (
              <div key={participant.id} className="classlive-participant">
                <span className="classlive-participant-name">
                  {participant.display_name}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span className="classlive-participant-role">
                    {participant.role === "host" ? "교사" : "학생"}
                  </span>
                  {participant.role !== 'host' && (
                    <button
                      className='classlive-tab classlive-danger'
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => handleKickParticipant(participant.id)}
                      disabled={kickingId === participant.id}
                    >
                      {kickingId === participant.id ? '내보내는 중...' : '퇴장'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
